import  { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import SidePanel from '../SidePanel/SidePanel'
import AuthNavBar from '../Dashboard/AuthNavBar'
import { EnvelopeIcon, ShieldCheckIcon, ComputerDesktopIcon, CreditCardIcon } from '@heroicons/react/24/outline'
import { fetchWithAuth } from '../../utils/apiClient'
import { useMe } from '../../context/MeContext'
import GeneralSettings from './GeneralSettings'
import EmailSettings from './EmailSettings'
import TelegramSettings from './TelegramSettings'
import SecuritySettings from './SecuritySettings'
import SessionsSettings from './SessionsSettings'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const TelegramIcon = ({ className }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="currentColor"
		className={className}
	>
		<path d="M12,2C6.5,2,2,6.5,2,12s4.5,10,10,10s10-4.5,10-10S17.5,2,12,2z M16.9,8.1l-1.7,8.2c-0.1,0.6-0.5,0.7-0.9,0.4l-2.6-2 c-0.6,0.6-1.2,1.1-1.3,1.3c-0.2,0.1-0.3,0.3-0.5,0.3c-0.3,0-0.3-0.2-0.4-0.4l-0.9-3L5.9,12c-0.6-0.2-0.6-0.6,0.1-0.9l10.2-3.9 C16.6,7.1,17.1,7.3,16.9,8.1z M14.5,9l-5.7,3.6l0.9,3l0.2-2l4.9-4.4C15.1,8.9,14.9,8.9,14.5,9z"/>
		<rect fill="none" width="24" height="24"/>
	</svg>
)

const Spinner = ({ className = 'h-4 w-4' }) => (
	<svg
		className={`animate-spin ${className}`}
		xmlns="http://www.w3.org/2000/svg"
		fill="none"
		viewBox="0 0 24 24"
	>
		<circle
			className="opacity-25"
			cx="12"
			cy="12"
			r="10"
			stroke="currentColor"
			strokeWidth="4"
		/>
		<path
			className="opacity-75"
			fill="currentColor"
			d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
		/>
	</svg>
)

function Settings() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [portalLoading, setPortalLoading] = useState(false)
	const navigate = useNavigate()
	const { tab } = useParams()

	const jwtToken = localStorage.getItem('jwtToken')
	const user = jwtToken ? jwtDecode(jwtToken) : null
	const isDevUser = user?.userType === 'dev'

	useEffect(() => {
		if (!tab) {
			navigate('/settings/email')
		}
		if (tab === 'subscription') {
			navigate('/settings/email')
		}
	}, [tab, navigate])

	const handleBillingClick = async () => {
		if (portalLoading) return
		setPortalLoading(true)
		try {
			const res = await fetchWithAuth('/api/stripe/portal', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					returnPath: window.location.pathname + window.location.search,
				}),
			})
			const body = await res.json()
			if (!res.ok || !body.url) {
				throw new Error(body.message || 'Could not open Stripe.')
			}
			window.location.assign(body.url)
		} catch (err) {
			console.error('[Billing] redirect failed:', err)
			setPortalLoading(false)
		}
	}

	const menuItems = [
		{ name: 'email', icon: EnvelopeIcon, component: EmailSettings },
		{ name: 'sessions', icon: ComputerDesktopIcon, component: SessionsSettings },
		{ name: 'security', icon: ShieldCheckIcon, component: SecuritySettings },
		{ name: 'telegram', icon: TelegramIcon, component: TelegramSettings },
	]

	const activeSection = tab || 'general'

	const handleTabChange = tabName => {
		navigate(`/settings/${tabName}`)
	}

	const ActiveComponent =
		menuItems.find(item => item.name === activeSection)?.component ||
		GeneralSettings

	return (
		<div className={`min-h-screen ${isLight ? 'bg-white text-gray-700' : 'bg-[#15202b] text-gray-200'}`}>
			<AuthNavBar />
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>
			<div className="max-w-7xl mx-auto pt-[5vh] pb-8 sm:px-6 lg:px-8">
				<div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
					<nav className="space-y-1">
						{menuItems.map(item => (
							<div
								key={item.name}
								className={`px-3 py-2 cursor-pointer rounded-lg transition-colors flex items-center text-sm font-medium ${
									activeSection === item.name
										? 'bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/20'
										: isLight
											? 'hover:bg-gray-50 text-gray-700 hover:text-gray-900'
											: 'hover:bg-white/5 text-gray-300 hover:text-gray-100'
								}`}
								onClick={() => handleTabChange(item.name)}
							>
								<item.icon className="h-5 w-5 mr-2.5" />
								{item.name.charAt(0).toUpperCase() + item.name.slice(1)}
							</div>
						))}

						<div
							className={`px-3 py-2 rounded-lg transition-colors flex items-center text-sm font-medium ${
								portalLoading
									? 'cursor-wait text-sky-400 bg-sky-500/10 ring-1 ring-sky-500/20'
									: isLight
										? 'cursor-pointer hover:bg-gray-50 text-gray-700 hover:text-gray-900'
										: 'cursor-pointer hover:bg-white/5 text-gray-300 hover:text-gray-100'
							}`}
							onClick={handleBillingClick}
						>
							<CreditCardIcon className="h-5 w-5 mr-2.5" />
							<span>Subscription</span>
							{portalLoading && (
								<Spinner className="h-4 w-4 ml-auto text-sky-400" />
							)}
						</div>
					</nav>
					<div className={`p-6 rounded-lg min-h-[24rem] ${
						isLight
							? 'bg-white ring-1 ring-gray-200 shadow-lg'
							: 'bg-[#1a2634] ring-1 ring-white/10 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
					}`}>
						<ActiveComponent />
					</div>
				</div>
			</div>
		</div>
	)
}

export default Settings
